// MobileAppManager.js - COMPLETE VERSION
// Full-featured: Messaging, Documents Upload, Family Export, Message History

import React, { useState, useEffect } from 'react';
import { Upload, CheckCircle, XCircle } from 'lucide-react';

// App-styled preview of how a message / announcement / event appears in the app.
// kind: 'message' | 'announcement' | 'event'.
function AppPreview({ kind, title, body, imageUri, pdfUri, pdfName, audienceLabel, push, sentBadge }) {
  return (
    <div className="mx-auto" style={{ maxWidth: 360 }}>
      <div className="text-[11px] text-gray-400 mb-2 text-center">Preview — how it appears in the app</div>
      {kind === 'event' ? (
        <div className="rounded-2xl overflow-hidden bg-blue-800 shadow">
          {imageUri ? (
            <img src={imageUri} alt="" className="w-full object-cover" style={{ maxHeight: 220 }} />
          ) : (
            <div className="p-5">
              <div className="text-white font-bold text-lg">{title || 'Event title'}</div>
              {body ? <div className="text-white/80 text-sm mt-1">{body}</div> : null}
            </div>
          )}
        </div>
      ) : kind === 'announcement' ? (
        <div className="rounded-2xl border border-gray-200 p-4 bg-white shadow flex gap-3 items-start">
          <div className="h-11 w-11 rounded-full bg-emerald-100 flex items-center justify-center text-xl shrink-0">📢</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900">{title || 'Announcement title'}</div>
            <div className="text-xs text-emerald-700 mt-0.5">Announcement</div>
            {pdfName ? <div className="mt-2 text-sm text-white bg-emerald-600 rounded-lg px-3 py-1.5 inline-block">📄 Open document</div> : null}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 p-4 bg-white shadow">
          <div className="flex gap-3 items-start">
            <div className="h-11 w-11 rounded-full bg-amber-100 flex items-center justify-center text-xl shrink-0">💬</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900">{title || 'Message title'}</div>
              {body ? <div className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">{body}</div> : null}
            </div>
          </div>
          {imageUri ? (
            <img src={imageUri} alt="" className="w-full rounded-lg mt-3 object-cover" style={{ maxHeight: 200 }} />
          ) : null}
          {pdfName && !pdfUri ? (
            <div className="mt-3 text-sm text-gray-700 bg-gray-100 rounded-lg px-3 py-2 inline-flex items-center gap-1">📄 {pdfName}</div>
          ) : null}
        </div>
      )}
      {/* Actual PDF preview — local file when composing, signed URL from history. */}
      {pdfUri ? (
        <div className="mt-3 rounded-lg overflow-hidden border border-gray-200 bg-white">
          <iframe src={pdfUri} title="PDF preview" style={{ width: '100%', height: 320, border: 'none' }} />
          {pdfName ? <div className="text-xs text-gray-500 px-3 py-1.5 border-t border-gray-100">📄 {pdfName}</div> : null}
        </div>
      ) : null}
      <div className="text-xs text-gray-500 mt-3 text-center">
        {audienceLabel ? `→ ${audienceLabel}` : ''}
        {audienceLabel && push != null ? ' · ' : ''}
        {push != null ? (push ? '📲 Push on' : '📱 No push') : ''}
      </div>
      {sentBadge ? (
        <div className="mt-2 text-center text-sm font-semibold text-green-600">✓ {sentBadge}</div>
      ) : null}
    </div>
  );
}

const MobileAppManager = ({ apiCall, showSuccess, showError }) => {
  const [preview, setPreview] = useState(null); // {kind,title,body,imageUri,pdfName,audienceLabel,push,sentBadge} | null
  const [activeTab, setActiveTab] = useState('send-message');
  // What the Send tab composes: 'message' | 'announcement' | 'event'.
  const [sendKind, setSendKind] = useState('message');
  const [families, setFamilies] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [announcementHistory, setAnnouncementHistory] = useState([]); // school_documents announcements, shown in History
  const [loading, setLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, message: '' });
  const [pwReset, setPwReset] = useState({ familyId: '', result: null, exists: null, checking: false });
  const [pwSearch, setPwSearch] = useState('');            // family search box for the reset tool
  const [pwSearchResults, setPwSearchResults] = useState([]);
  const [audience, setAudience] = useState('both'); // broadcast audience: both | school | daycare
  const [audienceIds, setAudienceIds] = useState(null); // FamilyIDs matching audience; null = all (both)
  const [recipientFamilies, setRecipientFamilies] = useState([]); // year-scoped families (w/ student names) for the message picker
  const [eventAudience, setEventAudience] = useState('both'); // event "all" broadcast: both | school | daycare
  const [daycareClasses, setDaycareClasses] = useState([]); // for the "by class" selector when audience=Daycare
  const [uploadProgress, setUploadProgress] = useState({ show: false, message: '', type: '' });

  const [familySearch, setFamilySearch] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [activeEvents, setActiveEvents] = useState(null); // events list for manage/delete

  // "Remove a student from the app" tool: look up a family's students on the
  // mobile DB, then delete the duplicate.
  const [mobileStuFamily, setMobileStuFamily] = useState('');
  const [mobileStuList, setMobileStuList] = useState(null); // null = not looked up yet

  const [messageForm, setMessageForm] = useState({
    recipientType: 'multiple-families',
    selectedFamilies: [],
    selectedClasses: [],
    title: '',
    message: '',
    messageType: 'general',
    sendPush: true,
    attachmentFile: null
  });

  const [exportForm, setExportForm] = useState({
    selectedYear: new Date().getFullYear(),
    autoCreateCredentials: true,
    defaultPassword: 'random' // all exports use random 6-digit passwords now (backend ignores this)
  });

  const [documentType, setDocumentType] = useState('report-card');
  
  const [reportCardForm, setReportCardForm] = useState({
    studentId: '',
    academicYear: new Date().getFullYear().toString(),
    title: '',
    pdfFile: null
  });

  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    pdfFile: null
  });

const [newsletterForm, setNewsletterForm] = useState({
  selectedClasses: [],  // NEW - array for multiple selection
  title: '',
  pdfFile: null
});

  // Events banner: pushed to the app's Home carousel; concerned families notified.
  const [eventForm, setEventForm] = useState({
    audience: 'all',            // 'all' | 'class' | 'family'
    selectedClasses: [],
    selectedFamilies: [],
    title: '',
    body: '',
    imageUrl: '',
    imageFile: null,            // uploaded banner image (wins over imageUrl)
    linkUrl: '',
    startsAt: '',
    endsAt: '',
    sendPush: true
  });

  // Academic year that the mobile syncs (roster export, absences/behavior,
  // agenda, notifications) operate on. Defaults to the system's current year,
  // but is overridable — during a year transition the app roster can still be
  // on the previous year (CurrentAcademicYear jumps ahead for registration).
  const [academicYears, setAcademicYears] = useState([]);
  const [syncYear, setSyncYear] = useState('');

  // Grade blocks: families with an outstanding balance are suggested; blocking
  // removes their report cards from the app and shows the message below.
  const [blockRows, setBlockRows] = useState([]);
  const [blockMsg, setBlockMsg] = useState('Bulletins indisponibles — merci de contacter le secrétariat.');
  const [blockSearch, setBlockSearch] = useState('');

  // Year-scoped export counts (families + students enrolled in the chosen year).
  const [exportStats, setExportStats] = useState({ families: 0, students: 0 });

  useEffect(() => {
    loadFamilies();
    loadClasses();
    loadStudents();
    loadMessageHistory();
    loadSyncYears();
    loadBlocks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBlocks = async () => {
    try {
      setBlockRows(await apiCall('/mobile/grade-block-suggestions'));
    } catch (error) {
      console.error('Error loading grade-block suggestions:', error);
    }
  };

  // Resolve which families match the chosen broadcast audience (School/Daycare),
  // so the family list + counts preview it. null = all (Both).
  useEffect(() => {
    if (audience === 'both') { setAudienceIds(null); return; }
    apiCall(`/mobile/families-by-audience/${audience}`)
      .then(r => setAudienceIds(r.familyIds || []))
      .catch(() => setAudienceIds([]));
  }, [audience, apiCall]);

  // Year-scoped recipient families (with enrolled student names) for the message
  // picker — matches the academic year chosen for export/sync (syncYear).
  useEffect(() => {
    if (!syncYear) return;
    apiCall(`/mobile/message-families/${encodeURIComponent(syncYear)}/${audience}`)
      .then(r => setRecipientFamilies(Array.isArray(r) ? r : []))
      .catch(() => setRecipientFamilies([]));
  }, [syncYear, audience, apiCall]);

  // Family type-ahead for the parent-credential reset tool (name / id / phone).
  useEffect(() => {
    const q = pwSearch.trim();
    if (q.length < 2) { setPwSearchResults([]); return; }
    let alive = true;
    const timer = setTimeout(() => {
      apiCall(`/mobile/family-search?q=${encodeURIComponent(q)}`)
        .then(r => { if (alive) setPwSearchResults(Array.isArray(r) ? r : []); })
        .catch(() => { if (alive) setPwSearchResults([]); });
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [pwSearch, apiCall]);

  // Check whether the picked family already has an app login, so the button can
  // say "Create" vs "Reset" before it's clicked. Debounced; only for a
  // plausibly-complete Family ID.
  useEffect(() => {
    const fid = pwReset.familyId.trim();
    if (fid.length < 4) { setPwReset((p) => (p.exists === null && !p.checking ? p : { ...p, exists: null, checking: false })); return undefined; }
    let alive = true;
    setPwReset((p) => ({ ...p, checking: true }));
    const timer = setTimeout(() => {
      apiCall(`/mobile/login-status/${encodeURIComponent(fid)}`)
        .then((r) => { if (alive) setPwReset((p) => (p.familyId.trim() === fid ? { ...p, exists: !!r.exists, checking: false } : p)); })
        .catch(() => { if (alive) setPwReset((p) => ({ ...p, checking: false })); });
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [pwReset.familyId, apiCall]);

  // Daycare classes for the "by class" selector under a Daycare audience.
  useEffect(() => {
    apiCall('/mobile/daycare-classes').then(setDaycareClasses).catch(() => setDaycareClasses([]));
  }, [apiCall]);

  // Reload the year-scoped export counts whenever the sync year changes.
  useEffect(() => {
    if (!syncYear) return;
    apiCall(`/mobile/export-stats/${encodeURIComponent(syncYear)}`)
      .then(setExportStats)
      .catch(() => setExportStats({ families: 0, students: 0 }));
  }, [apiCall, syncYear]);

  const blockFamily = async (familyId) => {
    try {
      const r = await apiCall('/mobile/grade-blocks', { method: 'POST', body: JSON.stringify({ familyId, reason: blockMsg }) });
      showSuccess(r.message);
      loadBlocks();
    } catch (e) { showError('Block failed: ' + e.message); }
  };

  const unblockFamily = async (familyId) => {
    try {
      const r = await apiCall(`/mobile/grade-blocks/${familyId}`, { method: 'DELETE' });
      showSuccess(r.message);
      loadBlocks();
    } catch (e) { showError('Unblock failed: ' + e.message); }
  };

  const loadSyncYears = async () => {
    try {
      const [years, current] = await Promise.all([
        apiCall('/system/academic-years'),
        apiCall('/system/current-academic-year').catch(() => ({ academicYear: null }))
      ]);
      const list = (years || []).map(y => y.AcademicYear);
      setAcademicYears(list);
      setSyncYear(current?.academicYear || list[0] || '');
    } catch (error) {
      console.error('Error loading academic years:', error);
    }
  };

  const loadFamilies = async () => {
    try {
      const data = await apiCall('/families');
      setFamilies(data);
    } catch (error) {
      console.error('Error loading families:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await apiCall('/classes/active');
      setClasses(data);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const data = await apiCall('/students');
      setStudents(data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadMessageHistory = async () => {
    try {
      const data = await apiCall('/mobile/messages/history');
      setMessageHistory(data);
    } catch (error) {
      console.error('Error loading message history:', error);
    }
    // Announcements live in a separate store (school_documents), so load them
    // alongside and show them in the same History tab.
    try {
      const a = await apiCall('/mobile/announcements');
      setAnnouncementHistory(Array.isArray(a?.announcements) ? a.announcements : []);
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  // Delete an announcement everywhere: the app's Announcements feed + its PDF.
  const handleDeleteAnnouncement = async (a) => {
    if (!window.confirm(`Delete announcement "${a.title}"?\n\nThis removes it from the app's Announcements for all recipients and deletes the PDF. This cannot be undone.`)) return;
    setLoading(true);
    try {
      await apiCall(`/mobile/announcements/${encodeURIComponent(a.document_id)}`, { method: 'DELETE' });
      showSuccess('Announcement deleted');
      loadMessageHistory();
    } catch (e) {
      showError('Delete failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Export families — driven page by page so the progress bar moves live, and
  // each page bulk-pushes to the mobile backend (fast).
  const handleExportFamilies = async (force = false) => {
    if (!window.confirm(force
        ? '⚠ FORCE export: this RESETS the app password for EVERY family — including families already exported — to the chosen default. Every family will appear in the CSV with a new password. Continue?'
        : 'This will export active families to the mobile app. New families get a login; families already exported KEEP their current password. Continue?')) {
      return;
    }

    const CHUNK = 200;
    setLoading(true);
    setExportProgress({ current: 0, total: 0, message: 'Creating parent logins…' });

    try {
      // Phase 1 — families (creates logins, accumulates the credentials CSV)
      let allCreds = [];
      let offset = 0, total = 0;
      do {
        const r = await apiCall('/mobile/export-chunk', {
          method: 'POST',
          body: JSON.stringify({
            phase: 'families', offset, limit: CHUNK, academicYear: syncYear,
            autoCreateCredentials: exportForm.autoCreateCredentials,
            defaultPassword: exportForm.defaultPassword,
            overwritePasswords: force
          })
        });
        total = r.total;
        allCreds = allCreds.concat(r.credentials || []);
        offset += r.processed;
        setExportProgress({ current: Math.min(offset, total), total, message: 'Creating parent logins…' });
        if (!r.processed) break;
      } while (offset < total);

      // Phase 2 — students (roster sync, no hashing → fast)
      let sOffset = 0, sTotal = 0;
      do {
        const r = await apiCall('/mobile/export-chunk', {
          method: 'POST',
          body: JSON.stringify({ phase: 'students', offset: sOffset, limit: CHUNK, academicYear: syncYear })
        });
        sTotal = r.total;
        sOffset += r.processed;
        setExportProgress({ current: Math.min(sOffset, sTotal), total: sTotal, message: 'Syncing students…' });
        if (!r.processed) break;
      } while (sOffset < sTotal);

      // Build + download the credentials CSV
      // Password column shows the real value only for accounts whose password
      // was actually set (new, or force-reset). Existing untouched families show
      // "(kept existing)" so we never hand out a password that won't work.
      let csv = 'Family ID,Username,Password,Parent Name,Phone,Email\n';
      allCreds.forEach(row => {
        const pw = row.PasswordChanged ? row.Password : '(kept existing)';
        csv += `${row.FamilyID},${row.Username},${pw},"${row.ParentName || ''}",${row.Phone || ''},${row.Email || ''}\n`;
      });
      downloadFile(csv, 'family-credentials.csv');

      const changed = allCreds.filter(r => r.PasswordChanged).length;
      setExportProgress({ current: allCreds.length, total: allCreds.length, message: 'Export complete!' });
      showSuccess(`Exported ${allCreds.length} families (${changed} new/updated password${changed === 1 ? '' : 's'}) and synced ${sTotal} students!`);
      setTimeout(() => setExportProgress({ current: 0, total: 0, message: '' }), 4000);
    } catch (error) {
      console.error('Export error:', error);
      setExportProgress({ current: 0, total: 0, message: '' });
      showError('Failed to export: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Export daycare — push all daycare children + provision daycare-ONLY family
  // logins (mixed families already have accounts). One call; returns a CSV.
  const handleExportDaycare = async (force = false) => {
    if (force && !window.confirm('⚠ FORCE daycare export: this RESETS the app password for every daycare-only family (including ones already exported). Continue?')) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/export-daycare', {
        method: 'POST',
        body: JSON.stringify({
          autoCreateCredentials: exportForm.autoCreateCredentials,
          defaultPassword: exportForm.defaultPassword,
          overwritePasswords: force,
        }),
      });
      if (r.credentialsFile && r.daycareOnlyFamilies > 0) {
        downloadFile(r.credentialsFile, 'daycare-credentials.csv');
      }
      showSuccess(`Pushed ${r.childrenPushed} daycare children; ${r.daycareOnlyFamilies} daycare-only families (${r.passwordsChanged} new/updated password${r.passwordsChanged === 1 ? '' : 's'}).`);
    } catch (error) {
      console.error('Daycare export error:', error);
      showError('Failed to export daycare: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Give / reset one family's app login with a fresh random password and show it.
  // Provisions the login on the spot if the family was never exported (new /
  // re-registered), so no full export is needed to onboard a single family.
  const handleResetPassword = async () => {
    if (!pwReset.familyId) { showError('Pick a family first'); return; }
    setLoading(true);
    try {
      const r = await apiCall('/mobile/reset-family-password', {
        method: 'POST',
        body: JSON.stringify({ familyId: pwReset.familyId }),
      });
      setPwReset((p) => ({ ...p, result: { username: r.username, password: r.password, created: r.created } }));
      showSuccess(r.created ? `Login created for ${r.username}` : `Password reset for ${r.username}`);
    } catch (error) {
      showError('Failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    const isAnnouncement = sendKind === 'announcement';

    // Announcement = a PDF that appears under Announcements (green megaphone) in
    // the app, plus a push titled with the announcement title. It goes to the
    // school_documents store (not the message feed), so the app already treats
    // it as a real announcement — no app changes needed. The Message field is
    // not used for an announcement (the PDF is the content).
    if (isAnnouncement) {
      if (!messageForm.title) { showError('Please enter an announcement title'); return; }
      const file = messageForm.attachmentFile;
      const isPdf = file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));
      if (!isPdf) { showError('Attach a PDF for the announcement.'); return; }
      setLoading(true);
      try {
        const pdfBase64 = await fileToBase64(file);
        // Honor the Audience selector: Both → all, School only → school, Daycare
        // only → daycare. The mobile side filters the feed + push to that segment.
        const recipientType = audience === 'school' ? 'school' : audience === 'daycare' ? 'daycare' : 'all';
        const r = await apiCall('/documents/upload-announcement', {
          method: 'POST',
          body: JSON.stringify({ title: messageForm.title, pdfBase64, sendPush: messageForm.sendPush, recipientType }),
        });
        showSuccess(`Announcement published${audience !== 'both' ? ` (${audience} only)` : ''}${messageForm.sendPush ? ` · ${r.pushNotificationsSent || 0} notification(s) sent` : ''}`);
        const attInput = document.getElementById('messageAttachment');
        if (attInput) attInput.value = '';
        setMessageForm({ ...messageForm, title: '', message: '', attachmentFile: null });
      } catch (e) {
        showError('Announcement failed: ' + e.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!messageForm.title) {
      showError('Please enter a title');
      return;
    }
    // Body can be blank when a file is attached (title + attachment only).
    if (!messageForm.message && !messageForm.attachmentFile) {
      showError('Add a message or attach a file');
      return;
    }

    if (!isAnnouncement && messageForm.recipientType === 'multiple-families' && messageForm.selectedFamilies.length === 0) {
      showError('Please select at least one family');
      return;
    }

    if (!isAnnouncement && messageForm.recipientType === 'specific-classes' && messageForm.selectedClasses.length === 0) {
      showError('Please select at least one class');
      return;
    }

    setLoading(true);
    try {
      let endpoint = '';
      let payload = {
        title: messageForm.title,
        message: messageForm.message,
        type: isAnnouncement ? 'announcement' : messageForm.messageType,
        sendPush: messageForm.sendPush
      };

      // Optional PDF/image attachment (parents see it in the message)
      if (messageForm.attachmentFile) {
        payload.attachmentBase64 = await fileToBase64(messageForm.attachmentFile);
        payload.attachmentName = messageForm.attachmentFile.name;
      }

      // A broadcast (announcement, or a message to all families) can be narrowed
      // to School-only or Daycare-only by resolving that audience's family set.
      const broadcast = isAnnouncement || messageForm.recipientType === 'all-families';
      if (broadcast && audience !== 'both') {
        const fam = await apiCall(`/mobile/families-by-audience/${audience}`);
        endpoint = '/mobile/messages/send-to-families';
        payload.familyIds = fam.familyIds || [];
        if (payload.familyIds.length === 0) { showError('No families match that audience.'); return; }
      } else if (isAnnouncement) {
        // Announcement = a message broadcast to ALL families.
        endpoint = '/mobile/messages/send-to-all';
      } else {
        switch (messageForm.recipientType) {
          case 'multiple-families':
          case 'outstanding-balance':
            endpoint = '/mobile/messages/send-to-families';
            payload.familyIds = messageForm.selectedFamilies;
            break;
          case 'all-families':
            endpoint = '/mobile/messages/send-to-all';
            break;
          case 'specific-classes':
            if (audience === 'daycare') {
              // Daycare "class" = the families of that class's children.
              const fam = await apiCall('/mobile/families-by-daycare-classes', {
                method: 'POST', body: JSON.stringify({ classNames: messageForm.selectedClasses }),
              });
              endpoint = '/mobile/messages/send-to-families';
              payload.familyIds = fam.familyIds || [];
              if (payload.familyIds.length === 0) { showError('No families in those daycare classes.'); return; }
            } else {
              endpoint = '/mobile/messages/send-to-class';
              payload.classNames = messageForm.selectedClasses;
            }
            break;
          default:
            showError('Unknown recipient type: ' + messageForm.recipientType);
            return;
        }
      }

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      showSuccess(`Message sent to ${response.messagesSent || response.totalFamilies} families!`);
      
      setMessageForm({
        ...messageForm,
        title: '',
        message: '',
        selectedFamilies: [],
        selectedClasses: [],
        attachmentFile: null
      });
      const attInput = document.getElementById('messageAttachment');
      if (attInput) attInput.value = '';

      loadMessageHistory();

    } catch (error) {
      console.error('Send message error:', error);
      showError('Failed to send message: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Document upload functions
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUploadReportCard = async () => {
    if (!reportCardForm.studentId || !reportCardForm.pdfFile) {
      showError('Please select a student and PDF file');
      return;
    }

    setLoading(true);
    setUploadProgress({ show: true, message: 'Converting PDF...', type: 'info' });

    try {
      const pdfBase64 = await fileToBase64(reportCardForm.pdfFile);
      setUploadProgress({ show: true, message: 'Uploading to cloud storage...', type: 'info' });

      await apiCall('/documents/upload-report-card', {
        method: 'POST',
        body: JSON.stringify({
          studentId: reportCardForm.studentId,
          academicYear: reportCardForm.academicYear,
          title: reportCardForm.title || `Report Card ${reportCardForm.academicYear}`,
          pdfBase64: pdfBase64
        })
      });

      setUploadProgress({ show: true, message: '✅ Upload successful!', type: 'success' });
      showSuccess('Report card uploaded successfully!');

      setReportCardForm({
        studentId: '',
        academicYear: new Date().getFullYear().toString(),
        title: '',
        pdfFile: null
      });
      document.getElementById('reportCardFile').value = '';

      setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress({ show: true, message: '❌ Upload failed!', type: 'error' });
      showError('Failed to upload: ' + error.message);
      setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.pdfFile) {
      showError('Please enter title and select PDF file');
      return;
    }

    setLoading(true);
    setUploadProgress({ show: true, message: 'Converting PDF...', type: 'info' });

    try {
      const pdfBase64 = await fileToBase64(announcementForm.pdfFile);
      setUploadProgress({ show: true, message: 'Uploading to cloud storage...', type: 'info' });

      await apiCall('/documents/upload-announcement', {
        method: 'POST',
        body: JSON.stringify({
          title: announcementForm.title,
          pdfBase64: pdfBase64
        })
      });

      setUploadProgress({ show: true, message: '✅ Upload successful!', type: 'success' });
      showSuccess('Announcement uploaded! All families can see it.');

      setAnnouncementForm({ title: '', pdfFile: null });
      document.getElementById('announcementFile').value = '';

      setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress({ show: true, message: '❌ Upload failed!', type: 'error' });
      showError('Failed to upload: ' + error.message);
      setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNewsletter = async () => {
  if (newsletterForm.selectedClasses.length === 0 || !newsletterForm.title || !newsletterForm.pdfFile) {
    showError('Please select at least one class, enter title, and select PDF file');
    return;
  }

  setLoading(true);
  setUploadProgress({ show: true, message: 'Converting PDF...', type: 'info' });

  try {
    const pdfBase64 = await fileToBase64(newsletterForm.pdfFile);
    setUploadProgress({ show: true, message: `Uploading to ${newsletterForm.selectedClasses.length} class${newsletterForm.selectedClasses.length > 1 ? 'es' : ''}...`, type: 'info' });

    // Upload to each selected class
    for (const className of newsletterForm.selectedClasses) {
      await apiCall('/documents/upload-class-newsletter', {
        method: 'POST',
        body: JSON.stringify({
          className: className,
          title: newsletterForm.title,
          pdfBase64: pdfBase64
        })
      });
    }

    setUploadProgress({ show: true, message: '✅ Upload successful!', type: 'success' });
    showSuccess(`Newsletter uploaded to ${newsletterForm.selectedClasses.length} class${newsletterForm.selectedClasses.length > 1 ? 'es' : ''}!`);

    setNewsletterForm({ selectedClasses: [], title: '', pdfFile: null });
    document.getElementById('newsletterFile').value = '';

    setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
  } catch (error) {
    console.error('Upload error:', error);
    setUploadProgress({ show: true, message: '❌ Upload failed!', type: 'error' });
    showError('Failed to upload: ' + error.message);
    setTimeout(() => setUploadProgress({ show: false, message: '', type: '' }), 3000);
  } finally {
    setLoading(false);
  }
};

  // ---- Preview helpers (app-styled) ----
  const messageAudienceLabel = () => {
    if (messageForm.recipientType === 'all-families') return 'All families';
    if (messageForm.recipientType === 'specific-classes') return messageForm.selectedClasses.length ? `Classes: ${messageForm.selectedClasses.join(', ')}` : 'Selected classes';
    if (messageForm.recipientType === 'outstanding-balance') return 'Families with a balance';
    return messageForm.selectedFamilies.length ? `${messageForm.selectedFamilies.length} family(ies)` : 'Selected families';
  };
  const eventAudienceLabel = () => {
    if (eventForm.audience === 'all') return eventAudience === 'both' ? 'All families' : eventAudience === 'school' ? 'All school families' : 'All daycare families';
    if (eventForm.audience === 'class') return eventForm.selectedClasses.length ? `Classes: ${eventForm.selectedClasses.join(', ')}` : 'Selected classes';
    return eventForm.selectedFamilies.length ? `${eventForm.selectedFamilies.length} family(ies)` : 'Selected families';
  };
  const previewMessage = () => {
    const kind = sendKind === 'announcement' ? 'announcement' : 'message';
    const file = messageForm.attachmentFile;
    let imageUri = null;
    let pdfName = null;
    let pdfUri = null;
    if (file) {
      const isImg = (file.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(file.name || '');
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
      if (isImg) { try { imageUri = URL.createObjectURL(file); } catch { imageUri = null; } }
      else if (isPdf) { pdfName = file.name; try { pdfUri = URL.createObjectURL(file); } catch { pdfUri = null; } }
    }
    setPreview({
      kind,
      title: messageForm.title,
      body: kind === 'message' ? messageForm.message : null,
      // Announcement = always a PDF; message = its optional image/PDF attachment.
      imageUri: kind === 'message' ? imageUri : null,
      pdfUri,
      pdfName,
      audienceLabel: kind === 'announcement' ? 'All families' : messageAudienceLabel(),
      push: messageForm.sendPush,
    });
  };
  const previewEvent = () => {
    let imageUri = null;
    if (eventForm.imageFile) { try { imageUri = URL.createObjectURL(eventForm.imageFile); } catch { imageUri = null; } }
    else if (eventForm.imageUrl) imageUri = eventForm.imageUrl;
    setPreview({ kind: 'event', title: eventForm.title, body: eventForm.body, imageUri, audienceLabel: eventAudienceLabel(), push: eventForm.sendPush });
  };
  const previewHistory = async (msg) => {
    setPreview({
      kind: msg.MessageType === 'announcement' ? 'announcement' : 'message',
      title: msg.Title, body: msg.Message,
      audienceLabel: `${msg.RecipientCount} ${msg.RecipientCount === 1 ? 'family' : 'families'}`,
      push: !!msg.PushSent,
      sentBadge: `Sent ${new Date(msg.SentAt).toLocaleString()}`,
    });
    // Pull the attachment (if any) for this send and show it in the preview.
    if (msg.SendId) {
      try {
        const r = await apiCall(`/mobile/messages/attachment/${encodeURIComponent(msg.SendId)}`);
        if (r && r.url) {
          setPreview((p) => (p ? {
            ...p,
            imageUri: r.kind === 'image' ? r.url : p.imageUri,
            pdfUri: r.kind === 'pdf' ? r.url : p.pdfUri,
            pdfName: r.kind === 'pdf' ? 'Attachment.pdf' : p.pdfName,
          } : p));
        }
      } catch { /* no attachment / ignore */ }
    }
  };
  const previewActiveEvent = (ev) => setPreview({
    kind: 'event', title: ev.title, body: ev.body, imageUri: ev.image_url || null,
    audienceLabel: ev.recipient_type === 'all' ? 'All families' : ev.recipient_type === 'school' ? 'All school' : ev.recipient_type === 'daycare' ? 'All daycare' : ev.recipient_type === 'class' ? `Class: ${ev.recipient_filter}` : `Family: ${ev.recipient_filter}`,
    push: null,
  });

  // Load active events (for the manage/delete list). Silent on failure so it
  // doesn't spam errors when the tab opens.
  const loadActiveEvents = async () => {
    try {
      const r = await apiCall('/mobile/events');
      setActiveEvents(r.events || []);
    } catch (e) {
      setActiveEvents([]);
    }
  };

  const handleDeleteEvent = async (id, title) => {
    if (!window.confirm(`Delete event "${title || id}" from the app? It disappears from families' banners.`)) return;
    setLoading(true);
    try {
      await apiCall(`/mobile/events/${id}`, { method: 'DELETE' });
      showSuccess('Event deleted');
      await loadActiveEvents();
    } catch (e) {
      showError('Delete failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh the events list whenever the Events tab is opened.
  useEffect(() => {
    if (sendKind === 'event') loadActiveEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendKind]);

  // Publish an event. One row per target so the app's per-class / per-family
  // matching stays exact (like newsletters).
  const handlePushEvent = async () => {
    if (!eventForm.title) { showError('Please enter an event title'); return; }
    let targets = [];
    if (eventForm.audience === 'all') {
      // ONE event row per whole-school segment (no per-family fan-out):
      // Both → 'all' (everyone), School only → 'school', Daycare only → 'daycare'.
      const rt = eventAudience === 'both' ? 'all' : eventAudience;
      targets = [{ recipientType: rt, recipientFilter: null }];
    } else if (eventForm.audience === 'class') {
      if (eventForm.selectedClasses.length === 0) { showError('Select at least one class'); return; }
      if (eventAudience === 'daycare') {
        const fam = await apiCall('/mobile/families-by-daycare-classes', {
          method: 'POST', body: JSON.stringify({ classNames: eventForm.selectedClasses }),
        });
        const ids = fam.familyIds || [];
        if (ids.length === 0) { showError('No families in those daycare classes.'); return; }
        targets = ids.map(f => ({ recipientType: 'family', recipientFilter: f }));
      } else {
        targets = eventForm.selectedClasses.map(c => ({ recipientType: 'class', recipientFilter: c }));
      }
    } else {
      if (eventForm.selectedFamilies.length === 0) { showError('Select at least one family'); return; }
      targets = eventForm.selectedFamilies.map(f => ({ recipientType: 'family', recipientFilter: f }));
    }
    setLoading(true);
    try {
      // Convert the banner image once (reused across per-target posts).
      let imageBase64 = null;
      let imageName = null;
      if (eventForm.imageFile) {
        imageBase64 = await fileToBase64(eventForm.imageFile);
        imageName = eventForm.imageFile.name;
      }

      let pushTotal = 0;
      for (const t of targets) {
        const resp = await apiCall('/mobile/events', {
          method: 'POST',
          body: JSON.stringify({
            title: eventForm.title,
            body: eventForm.body || null,
            imageUrl: eventForm.imageUrl || null,
            imageBase64,
            imageName,
            linkUrl: eventForm.linkUrl || null,
            recipientType: t.recipientType,
            recipientFilter: t.recipientFilter,
            startsAt: eventForm.startsAt || null,
            endsAt: eventForm.endsAt || null,
            sendPush: eventForm.sendPush
          })
        });
        pushTotal += resp.pushNotificationsSent || 0;
      }
      showSuccess(`Event published${eventForm.sendPush ? ` · ${pushTotal} notification(s) sent` : ''}`);
      setEventForm({ ...eventForm, title: '', body: '', imageUrl: '', imageFile: null, linkUrl: '', startsAt: '', endsAt: '', selectedClasses: [], selectedFamilies: [] });
      const evtImg = document.getElementById('eventImageFile');
      if (evtImg) evtImg.value = '';
      loadActiveEvents();
    } catch (error) {
      showError('Failed to publish event: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Push per-family module balances (Bus, Activities, ...) to the mobile app.
  const handleSyncBalances = async () => {
    if (!window.confirm("Push every family's balance breakdown (Bus, Activities, Afterschool, ...) to the mobile app?")) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/sync-balances', { method: 'POST', body: JSON.stringify({}) });
      showSuccess(`Balances synced for ${r.familiesWithBalances} family(ies)`);
    } catch (e) {
      showError('Balance sync failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reconcile the year's payment plans with SIS, then push next-due/overdue
  // to the app. School-network only (needs SIS).
  const handleSyncPaymentPlans = async () => {
    if (!window.confirm(`Reconcile ${syncYear} payment plans with SIS and push next-due / overdue to the app? (school network only)`)) return;
    setLoading(true);
    try {
      const r = await apiCall(`/registration/mobile/sync-payment-plans/${encodeURIComponent(syncYear)}`, { method: 'POST', body: JSON.stringify({}) });
      showSuccess(`Payment plans synced (${r.academicYear}): ${r.families} family(ies)`);
    } catch (e) {
      showError('Payment plan sync failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Manually run the payment-notification poll (same job the scheduler runs
  // when PAYMENT_NOTIFY_ENABLED=true). Notifies new payments past the 10-min
  // grace + any post-grace voids.
  const handleNotifyPayments = async () => {
    if (!window.confirm('Send push notifications now for new payments (past the 10-min grace) and any cancellations?')) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/notify-payments', { method: 'POST', body: JSON.stringify({}) });
      const sis = r.sis || {};
      const sisPart = sis.reachable === false ? ' · SIS unreachable'
        : ` · SIS: ${sis.paymentsNotified || 0} payment(s), ${sis.refundsNotified || 0} refund(s)`;
      showSuccess(`Payments: ${r.paymentsNotified} sent · ${r.voidsNotified} cancellation(s)${sisPart}`);
    } catch (e) {
      showError('Payment notify failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Push all students' absences + behavior to the mobile app.
  const handleSyncAcademic = async () => {
    if (!window.confirm("Sync all students' absences and behavior to the mobile app?")) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/sync-academic', { method: 'POST', body: JSON.stringify({ academicYear: syncYear }) });
      showSuccess(`Academic data synced (${r.academicYear}): ${r.studentsSynced} students · ${r.absences || 0} absences · ${r.behavior || 0} behavior records`);
    } catch (e) {
      showError('Academic sync failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Push the homework agenda (recent window) to the app. Fans section-level
  // entries out to each student; the app shows a rolling 7-day view.
  const handleSyncAgenda = async () => {
    if (!window.confirm("Sync the recent homework agenda to the mobile app?")) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/sync-agenda', { method: 'POST', body: JSON.stringify({ academicYear: syncYear }) });
      showSuccess(`Agenda synced (${r.academicYear}): ${r.entriesPushed || 0} entrie(s) pushed to the app`);
    } catch (e) {
      showError('Agenda sync failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Announce NEW agenda — one push per family (generic). Already-announced
  // entries are skipped server-side, so this never re-notifies.
  const handleNotifyAgenda = async () => {
    if (!window.confirm('Send a "new homework" notification to families with new agenda entries?')) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/notify-agenda', { method: 'POST', body: JSON.stringify({ academicYear: syncYear }) });
      showSuccess(`Agenda notification sent (${r.academicYear}): ${r.familiesNotified} family(ies)`);
    } catch (e) {
      showError('Agenda notify failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // "Remove a student from the app" — look up a family's students on the MOBILE
  // DB so duplicates are visible, then delete the right one. The student sync is
  // upsert-only (never prunes), so a child deleted in SQL stays on phones until
  // removed here. Does NOT touch SQL Server.
  const lookupMobileFamilyStudents = async () => {
    const fam = mobileStuFamily.trim();
    if (!fam) return;
    setLoading(true);
    try {
      const r = await apiCall(`/mobile/family-students/${encodeURIComponent(fam)}`);
      setMobileStuList(r.students || []);
    } catch (e) {
      showError('Lookup failed: ' + e.message);
      setMobileStuList(null);
    } finally {
      setLoading(false);
    }
  };

  const deleteMobileStudent = async (id, label) => {
    if (!window.confirm(`Delete ${label} (${id}) from the MOBILE app DB?\n\nRemoves the student, grades, absences, behavior, documents and balances from the app. Cannot be undone. SQL Server is NOT affected.`)) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/delete-student', { method: 'POST', body: JSON.stringify({ studentId: id }) });
      showSuccess(r.deleted ? `Removed ${id} from the app.` : `No student ${id} found in the app DB.`);
      const r2 = await apiCall(`/mobile/family-students/${encodeURIComponent(mobileStuFamily.trim())}`);
      setMobileStuList(r2.students || []);
    } catch (e) {
      showError('Delete failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Push the current year's active bus roster to the app — unlocks the Bus tab
  // and tells it which bus each child rides. Safe to re-run (full replace).
  const handleSyncBusSubscriptions = async () => {
    if (!window.confirm("Push this year's active bus enrollments to the mobile app?")) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/sync-bus-subscriptions', { method: 'POST', body: JSON.stringify({}) });
      showSuccess(`Bus subscriptions synced: ${r.subscriptions} enrollment(s)`);
    } catch (e) {
      showError('Bus subscription sync failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Pull parent-set drop-off pins from the app into SQL Server for the web view.
  const handlePullBusDropoffs = async () => {
    setLoading(true);
    try {
      const r = await apiCall('/mobile/pull-bus-dropoffs', { method: 'POST', body: JSON.stringify({}) });
      showSuccess(r.pulled ? `Pulled ${r.pulled} drop-off pin(s) from the app` : 'No new drop-off pins to pull');
    } catch (e) {
      showError('Drop-off pull failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const toggleFamily = (familyId) => {
    const selected = messageForm.selectedFamilies.includes(familyId)
      ? messageForm.selectedFamilies.filter(id => id !== familyId)
      : [...messageForm.selectedFamilies, familyId];
    setMessageForm({ ...messageForm, selectedFamilies: selected });
  };

  const toggleClass = (className) => {
    const selected = messageForm.selectedClasses.includes(className)
      ? messageForm.selectedClasses.filter(c => c !== className)
      : [...messageForm.selectedClasses, className];
    setMessageForm({ ...messageForm, selectedClasses: selected });
  };

  // Narrow the family pool to the chosen broadcast audience (null = all).
  // Year-scoped families (enrolled in the chosen year) with student names.
  const audienceFamilies = recipientFamilies;
  // Class pool for the "by class" selector: daycare classes under a Daycare
  // audience (normalized to ClassName/StudentCount so the UI is unchanged).
  const audienceClasses = audience === 'daycare'
    ? daycareClasses.map(c => ({ ClassName: c.ClassName, StudentCount: c.ChildCount }))
    : classes;

  const selectAllFamilies = () => {
    const filtered = audienceFamilies.filter(f =>
      !familySearch || 
      f.FamilyID.toLowerCase().includes(familySearch.toLowerCase()) ||
      (f.FatherName && f.FatherName.toLowerCase().includes(familySearch.toLowerCase())) ||
      (f.MotherName && f.MotherName.toLowerCase().includes(familySearch.toLowerCase()))
    );
    setMessageForm({ ...messageForm, selectedFamilies: filtered.map(f => f.FamilyID) });
  };

  const clearAllFamilies = () => {
    setMessageForm({ ...messageForm, selectedFamilies: [] });
  };

  const selectAllClasses = () => {
    const filtered = audienceClasses.filter(c =>
      !classSearch ||
      c.ClassName.toLowerCase().includes(classSearch.toLowerCase())
    );
    setMessageForm({ ...messageForm, selectedClasses: filtered.map(c => c.ClassName) });
  };

  const clearAllClasses = () => {
    setMessageForm({ ...messageForm, selectedClasses: [] });
  };

  const getRecipientCount = () => {
    if (sendKind === 'announcement') {
      return audienceFamilies.length;
    }
    if (messageForm.recipientType === 'all-families') {
      return audienceFamilies.length;
    }
    if (messageForm.recipientType === 'multiple-families' || messageForm.recipientType === 'outstanding-balance') {
      return messageForm.selectedFamilies.length;
    }
    if (messageForm.recipientType === 'specific-classes') {
      return audienceClasses
        .filter(c => messageForm.selectedClasses.includes(c.ClassName))
        .reduce((sum, c) => sum + (c.StudentCount || 0), 0);
    }
    return 0;
  };

  const filteredFamilies = audienceFamilies.filter(f =>
    !familySearch ||
    f.FamilyID.toLowerCase().includes(familySearch.toLowerCase()) ||
    (f.FatherName && f.FatherName.toLowerCase().includes(familySearch.toLowerCase())) ||
    (f.MotherName && f.MotherName.toLowerCase().includes(familySearch.toLowerCase()))
  );

  // Class pool for whichever tab is active: daycare classes under a Daycare
  // audience (events use eventAudience, messages use audience).
  const dcClassesNorm = daycareClasses.map(c => ({ ClassName: c.ClassName, StudentCount: c.ChildCount }));
  const classPool = sendKind === 'event'
    ? (eventAudience === 'daycare' ? dcClassesNorm : classes)
    : audienceClasses;
  const filteredClasses = classPool.filter(c =>
    !classSearch ||
    c.ClassName.toLowerCase().includes(classSearch.toLowerCase())
  );

  // Grade blocks: which families are currently blocked, and manual-search matches
  // so any family can be blocked (not just the outstanding-balance suggestions).
  const blockedIds = new Set(blockRows.filter(r => r.Blocked).map(r => String(r.FamilyID)));
  const blockMatches = blockSearch.trim()
    ? families.filter(f =>
        f.FamilyID.toLowerCase().includes(blockSearch.toLowerCase()) ||
        (f.FatherName && f.FatherName.toLowerCase().includes(blockSearch.toLowerCase())) ||
        (f.MotherName && f.MotherName.toLowerCase().includes(blockSearch.toLowerCase()))
      ).slice(0, 8)
    : [];

  // Render tabs
  const renderSendMessageTab = () => {
    const titleChars = messageForm.title.length;
    const messageChars = messageForm.message.length;

    return (
      <div className="space-y-6">
        {/* What are we sending? */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Type:</label>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {[
              { k: 'message', label: '💬 Message' },
              { k: 'announcement', label: '📢 Announcement' },
              { k: 'event', label: '🎉 Event' },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setSendKind(o.k)}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${
                  sendKind === o.k ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {sendKind === 'message'
              ? 'A direct message to chosen families/classes (in-app + optional push).'
              : sendKind === 'announcement'
              ? 'Attach a PDF below — it shows under Announcements (green megaphone) in the app for the chosen Audience, with a push titled with your title. The Message field is not used.'
              : 'A banner on the app Home screen for the chosen audience.'}
          </p>

          {(sendKind === 'announcement' || sendKind === 'message') && (
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Audience</label>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                {[['both', 'Both'], ['school', 'School only'], ['daycare', 'Daycare only']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setAudience(v)}
                    className={`px-3 py-1.5 text-sm ${audience === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{l}</button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Applies when broadcasting (announcement, or a message to all families).</p>
            </div>
          )}
        </div>

        {sendKind === 'event' ? renderEventsTab() : (
        <div className="space-y-6">
        {sendKind === 'announcement' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            📢 This announcement goes to <strong>{audienceFamilies.length} families</strong>{audience !== 'both' ? ` (${audience} only)` : ''}.
          </div>
        )}
        {sendKind === 'message' && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Send To:</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMessageForm({ ...messageForm, recipientType: 'multiple-families', selectedFamilies: [], selectedClasses: [] })}
              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                messageForm.recipientType === 'multiple-families'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-1">👨‍👩‍👧‍👦</div>
              <div className="font-semibold">Specific Families</div>
              <div className="text-xs text-gray-600">Select one or more</div>
            </button>

            <button
              onClick={() => setMessageForm({ ...messageForm, recipientType: 'all-families', selectedFamilies: [], selectedClasses: [] })}
              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                messageForm.recipientType === 'all-families'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-1">📢</div>
              <div className="font-semibold">All Families</div>
              <div className="text-xs text-gray-600">Broadcast ({audienceFamilies.length})</div>
            </button>

            <button
              onClick={() => setMessageForm({ ...messageForm, recipientType: 'specific-classes', selectedFamilies: [], selectedClasses: [] })}
              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                messageForm.recipientType === 'specific-classes'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-1">🎓</div>
              <div className="font-semibold">By Class</div>
              <div className="text-xs text-gray-600">Select classes</div>
            </button>

            <button
              onClick={() => {
                const outstandingFamilies = families.filter(f => f.OutstandingBalance && f.OutstandingBalance > 0).map(f => f.FamilyID);
                setMessageForm({ 
                  ...messageForm, 
                  recipientType: 'outstanding-balance',
                  selectedFamilies: outstandingFamilies,
                  selectedClasses: []
                });
              }}
              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                messageForm.recipientType === 'outstanding-balance'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-1">💰</div>
              <div className="font-semibold">Outstanding</div>
              <div className="text-xs text-gray-600">Pending payments</div>
            </button>
          </div>
        </div>
        )}

        {sendKind === 'message' && (messageForm.recipientType === 'multiple-families' || messageForm.recipientType === 'outstanding-balance') && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Select Families ({messageForm.selectedFamilies.length} selected · {audienceFamilies.length} in {syncYear || '—'})
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={syncYear}
                  onChange={(e) => setSyncYear(e.target.value)}
                  className="text-xs border border-gray-300 rounded px-2 py-1"
                  title="Academic year — families enrolled in this year"
                >
                  {academicYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <button onClick={selectAllFamilies} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  Select All
                </button>
                <button onClick={clearAllFamilies} className="text-xs text-gray-600 hover:text-gray-800 font-semibold">
                  Clear
                </button>
              </div>
            </div>

            <input
              type="text"
              placeholder="🔍 Search families by ID or name..."
              value={familySearch}
              onChange={(e) => setFamilySearch(e.target.value)}
              className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />

            <div className="border border-gray-300 rounded-lg max-h-80 overflow-y-auto">
              {filteredFamilies.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No families found</div>
              ) : (
                filteredFamilies.map(family => (
                  <div
                    key={family.FamilyID}
                    onClick={() => toggleFamily(family.FamilyID)}
                    className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                      messageForm.selectedFamilies.includes(family.FamilyID) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={messageForm.selectedFamilies.includes(family.FamilyID)}
                        onChange={() => {}}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">
                          Family {family.FamilyID} - {family.FatherName || family.MotherName || 'Unknown'}
                        </div>
                        {family.StudentNames && (
                          <div className="text-xs text-gray-500">🎒 {family.StudentNames}</div>
                        )}
                        <div className="text-sm text-gray-600">
                          {family.StudentCount} student{family.StudentCount !== 1 ? 's' : ''}
                          {family.Phone1 && ` • ${family.Phone1}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {sendKind === 'message' && messageForm.recipientType === 'specific-classes' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Select Classes ({messageForm.selectedClasses.length} selected)
              </label>
              <div className="space-x-2">
                <button onClick={selectAllClasses} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  Select All
                </button>
                <button onClick={clearAllClasses} className="text-xs text-gray-600 hover:text-gray-800 font-semibold">
                  Clear
                </button>
              </div>
            </div>

            <input
              type="text"
              placeholder="🔍 Search classes..."
              value={classSearch}
              onChange={(e) => setClassSearch(e.target.value)}
              className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />

            <div className="grid grid-cols-2 gap-3">
              {filteredClasses.map(cls => (
                <div
                  key={cls.ClassName}
                  onClick={() => toggleClass(cls.ClassName)}
                  className={`p-3 border-2 rounded-lg cursor-pointer hover:border-blue-400 transition-all ${
                    messageForm.selectedClasses.includes(cls.ClassName)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={messageForm.selectedClasses.includes(cls.ClassName)}
                      onChange={() => {}}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{cls.ClassName}</div>
                      <div className="text-sm text-gray-600">{cls.StudentCount} students</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {getRecipientCount() > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">✉️</span>
              <div>
                <div className="font-semibold text-green-900">
                  Ready to send to {getRecipientCount()} {getRecipientCount() === 1 ? 'family' : 'families'}
                </div>
                <div className="text-sm text-green-700">
                  {messageForm.sendPush ? '📲 Push notification enabled' : '📱 In-app message only'}
                </div>
              </div>
            </div>
          </div>
        )}

        {sendKind === 'message' && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Message Priority:</label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'general', label: 'General', icon: '📝' },
              { value: 'urgent', label: 'Urgent', icon: '⚠️' },
              { value: 'payment', label: 'Payment', icon: '💰' },
              { value: 'absence', label: 'Absence', icon: '📅' }
            ].map(type => (
              <button
                key={type.value}
                onClick={() => setMessageForm({ ...messageForm, messageType: type.value })}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  messageForm.messageType === type.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className="text-2xl">{type.icon}</div>
                <div className="text-sm font-semibold mt-1">{type.label}</div>
              </button>
            ))}
          </div>
        </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700">Title:</label>
            <span className={`text-xs ${titleChars > 100 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
              {titleChars}/100
            </span>
          </div>
          <input 
            type="text"
            value={messageForm.title}
            onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value.slice(0, 100) })}
            placeholder="Enter message title"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Announcements are title + PDF only — the message body isn't used, so
            hide it for the Announcement kind. */}
        {sendKind !== 'announcement' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700">Message{messageForm.attachmentFile ? ' (optional — file attached)' : ''}:</label>
            <span className={`text-xs ${messageChars > 500 ? 'text-orange-600' : 'text-gray-500'}`}>
              {messageChars} characters
            </span>
          </div>
          <textarea
            value={messageForm.message}
            onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
            placeholder="Enter your message here..."
            rows="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {sendKind === 'announcement' ? 'PDF (required):' : 'Attachment (optional — PDF or image):'}
          </label>
          <input
            id="messageAttachment"
            type="file"
            accept={sendKind === 'announcement' ? '.pdf' : '.pdf,image/*'}
            onChange={(e) => setMessageForm({ ...messageForm, attachmentFile: e.target.files[0] || null })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
          {sendKind === 'announcement' && (
            <p className="text-[11px] text-gray-500 mt-1">Announcements are a PDF. For a picture flyer, use the Events tab instead (shows as a banner).</p>
          )}
          {messageForm.attachmentFile && (
            <p className="text-sm text-gray-600 mt-2">
              📎 {messageForm.attachmentFile.name} ({(messageForm.attachmentFile.size / 1024).toFixed(0)} KB)
            </p>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sendPush"
              checked={messageForm.sendPush}
              onChange={(e) => setMessageForm({ ...messageForm, sendPush: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="sendPush" className="ml-3 flex-1">
              <div className="font-semibold text-gray-900">Send Push Notification</div>
              <div className="text-sm text-gray-600">
                {messageForm.sendPush 
                  ? '📲 Parents will be notified immediately' 
                  : '📱 Message will appear in app only'}
              </div>
            </label>
          </div>
        </div>

        <button
          onClick={previewMessage}
          disabled={!messageForm.title}
          className="w-full mb-3 bg-white border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
        >
          👁️ Preview
        </button>
        <button
          onClick={handleSendMessage}
          disabled={loading || getRecipientCount() === 0}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span>⏳ Sending...</span>
          ) : (
            <span>📤 Send to {getRecipientCount()} {getRecipientCount() === 1 ? 'Family' : 'Families'}</span>
          )}
        </button>

        {sendKind === 'message' && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-bold text-gray-800 mb-3">✨ Quick Templates</h3>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => setMessageForm({
                ...messageForm,
                title: 'School Closure - Weather',
                message: 'Dear Parents,\n\nDue to severe weather conditions, the school will be closed tomorrow. All classes and activities are cancelled. Stay safe!',
                messageType: 'urgent',
                sendPush: true
              })}
              className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="text-2xl mb-1">🌧️</div>
              <div className="font-semibold text-sm">Weather Closure</div>
            </button>
            
            <button 
              onClick={() => setMessageForm({
                ...messageForm,
                title: 'Payment Reminder',
                message: 'Dear Parents,\n\nThis is a friendly reminder that your account has an outstanding balance. Please contact the accounting office.\n\nThank you.',
                messageType: 'payment',
                sendPush: false
              })}
              className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="text-2xl mb-1">💰</div>
              <div className="font-semibold text-sm">Payment Reminder</div>
            </button>

            <button 
              onClick={() => setMessageForm({
                ...messageForm,
                title: 'Upcoming Event',
                message: 'Dear Parents,\n\nWe would like to remind you about our upcoming school event.\n\nDate: [Add Date]\nTime: [Add Time]\nLocation: [Add Location]',
                messageType: 'general',
                sendPush: true
              })}
              className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="text-2xl mb-1">🎉</div>
              <div className="font-semibold text-sm">Event Reminder</div>
            </button>

            <button 
              onClick={() => setMessageForm({
                ...messageForm,
                title: 'Important Notice',
                message: 'Dear Parents,\n\n[Your important message here]\n\nFor more information, please contact the school office.\n\nThank you.',
                messageType: 'urgent',
                sendPush: true
              })}
              className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="text-2xl mb-1">📢</div>
              <div className="font-semibold text-sm">Important Notice</div>
            </button>
          </div>
        </div>
        )}
        </div>
        )}
      </div>
    );
  };

  const renderDocumentsTab = () => (
    <div className="space-y-6">
      {uploadProgress.show && (
        <div className={`rounded-lg p-4 ${
          uploadProgress.type === 'success' ? 'bg-green-50 border border-green-200' :
          uploadProgress.type === 'error' ? 'bg-red-50 border border-red-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center gap-3">
            {uploadProgress.type === 'success' && <CheckCircle className="text-green-600" size={24} />}
            {uploadProgress.type === 'error' && <XCircle className="text-red-600" size={24} />}
            {uploadProgress.type === 'info' && <Upload className="text-blue-600 animate-pulse" size={24} />}
            <span className={`font-semibold ${
              uploadProgress.type === 'success' ? 'text-green-900' :
              uploadProgress.type === 'error' ? 'text-red-900' :
              'text-blue-900'
            }`}>
              {uploadProgress.message}
            </span>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">Upload Type:</label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setDocumentType('report-card')}
            className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
              documentType === 'report-card'
                ? 'border-blue-500 bg-blue-50 text-blue-900'
                : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-1">📄</div>
            <div className="font-semibold">Report Card</div>
            <div className="text-xs text-gray-600">One per student</div>
          </button>

          <button
            onClick={() => setDocumentType('announcement')}
            className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
              documentType === 'announcement'
                ? 'border-green-500 bg-green-50 text-green-900'
                : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-1">📢</div>
            <div className="font-semibold">Announcement</div>
            <div className="text-xs text-gray-600">For all families</div>
          </button>

          <button
            onClick={() => setDocumentType('newsletter')}
            className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
              documentType === 'newsletter'
                ? 'border-purple-500 bg-purple-50 text-purple-900'
                : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
          >
            <div className="text-2xl mb-1">🎓</div>
            <div className="font-semibold">Newsletter</div>
            <div className="text-xs text-gray-600">For one class</div>
          </button>
        </div>
      </div>

      {documentType === 'report-card' && (
        <div className="space-y-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Student:</label>
            <select
              value={reportCardForm.studentId}
              onChange={(e) => setReportCardForm({ ...reportCardForm, studentId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Student --</option>
              {students.map(student => (
                <option key={student.StudentID} value={student.StudentID}>
                  {student.StudentID} - {student.FirstName} {student.LastName} ({student.ClassName})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Academic Year:</label>
            <input
              type="text"
              value={reportCardForm.academicYear}
              onChange={(e) => setReportCardForm({ ...reportCardForm, academicYear: e.target.value })}
              placeholder="e.g., 2024-2025"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Title (Optional):</label>
            <input
              type="text"
              value={reportCardForm.title}
              onChange={(e) => setReportCardForm({ ...reportCardForm, title: e.target.value })}
              placeholder="e.g., First Trimester Report"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Upload PDF:</label>
            <input
              id="reportCardFile"
              type="file"
              accept=".pdf"
              onChange={(e) => setReportCardForm({ ...reportCardForm, pdfFile: e.target.files[0] })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            {reportCardForm.pdfFile && (
              <p className="text-sm text-gray-600 mt-2">
                📄 {reportCardForm.pdfFile.name} ({(reportCardForm.pdfFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <button
            onClick={handleUploadReportCard}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? '⏳ Uploading...' : '📤 Upload Report Card'}
          </button>
        </div>
      )}

      {documentType === 'announcement' && (
        <div className="space-y-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Title:</label>
            <input
              type="text"
              value={announcementForm.title}
              onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
              placeholder="e.g., Holiday Schedule 2025"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Upload PDF:</label>
            <input
              id="announcementFile"
              type="file"
              accept=".pdf"
              onChange={(e) => setAnnouncementForm({ ...announcementForm, pdfFile: e.target.files[0] })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            {announcementForm.pdfFile && (
              <p className="text-sm text-gray-600 mt-2">
                📄 {announcementForm.pdfFile.name} ({(announcementForm.pdfFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <button
            onClick={handleUploadAnnouncement}
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? '⏳ Uploading...' : '📤 Upload to All Families'}
          </button>
        </div>
      )}

{documentType === 'newsletter' && (
  <div className="space-y-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-gray-700">
          Select Classes ({newsletterForm.selectedClasses.length} selected):
        </label>
        <div className="space-x-2">
          <button
            onClick={() => setNewsletterForm({ ...newsletterForm, selectedClasses: classes.map(c => c.ClassName) })}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
          >
            Select All
          </button>
          <button
            onClick={() => setNewsletterForm({ ...newsletterForm, selectedClasses: [] })}
            className="text-xs text-gray-600 hover:text-gray-800 font-semibold"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-3">
        {classes.map(cls => (
          <div
            key={cls.ClassName}
            onClick={() => {
              const selected = newsletterForm.selectedClasses.includes(cls.ClassName)
                ? newsletterForm.selectedClasses.filter(c => c !== cls.ClassName)
                : [...newsletterForm.selectedClasses, cls.ClassName];
              setNewsletterForm({ ...newsletterForm, selectedClasses: selected });
            }}
            className={`p-3 border-2 rounded-lg cursor-pointer hover:border-purple-400 transition-all ${
              newsletterForm.selectedClasses.includes(cls.ClassName)
                ? 'border-purple-500 bg-purple-100'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={newsletterForm.selectedClasses.includes(cls.ClassName)}
                onChange={() => {}}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded mr-3"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{cls.ClassName}</div>
                <div className="text-sm text-gray-600">{cls.StudentCount} students</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Title:</label>
      <input
        type="text"
        value={newsletterForm.title}
        onChange={(e) => setNewsletterForm({ ...newsletterForm, title: e.target.value })}
        placeholder="e.g., December Newsletter"
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Upload PDF:</label>
      <input
        id="newsletterFile"
        type="file"
        accept=".pdf"
        onChange={(e) => setNewsletterForm({ ...newsletterForm, pdfFile: e.target.files[0] })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
      />
      {newsletterForm.pdfFile && (
        <p className="text-sm text-gray-600 mt-2">
          📄 {newsletterForm.pdfFile.name} ({(newsletterForm.pdfFile.size / 1024).toFixed(2)} KB)
        </p>
      )}
    </div>

    <button
      onClick={handleUploadNewsletter}
      disabled={loading || newsletterForm.selectedClasses.length === 0}
      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
    >
      {loading 
        ? '⏳ Uploading...' 
        : newsletterForm.selectedClasses.length === 0 
          ? '📤 Select Classes to Upload'
          : `📤 Upload to ${newsletterForm.selectedClasses.length} Class${newsletterForm.selectedClasses.length !== 1 ? 'es' : ''}`
      }
    </button>

    <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
      <p className="text-sm text-purple-900">
        💡 <strong>Tip:</strong> The same PDF will be uploaded once and shared with all selected classes. Families will only see the newsletter if they have students in one of the selected classes.
      </p>
    </div>
  </div>
)}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-2">ℹ️ How it works:</h3>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li><strong>Report Cards:</strong> Parents see their child's report in the app</li>
          <li><strong>Announcements:</strong> All families see it immediately</li>
          <li><strong>Newsletters:</strong> Only families in that class see it</li>
          <li><strong>Storage:</strong> Files stored securely in Cloudflare R2 (FREE)</li>
          <li><strong>Access:</strong> Temporary signed URLs expire after 5 minutes</li>
        </ul>
      </div>
    </div>
  );

  const renderEventsTab = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
        🎉 Events appear as an auto-scrolling banner on the app's Home screen. Only families with a child in the chosen audience see them.
      </div>

      {/* Audience */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">Audience:</label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { value: 'all', icon: '📢', label: 'All Families', sub: `Everyone (${families.length})` },
            { value: 'class', icon: '🎓', label: 'By Class', sub: 'One or more classes' },
            { value: 'family', icon: '👨‍👩‍👧', label: 'Specific Families', sub: 'Pick families' },
          ].map(a => (
            <button
              key={a.value}
              onClick={() => setEventForm({ ...eventForm, audience: a.value })}
              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                eventForm.audience === a.value ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-1">{a.icon}</div>
              <div className="font-semibold">{a.label}</div>
              <div className="text-xs text-gray-600">{a.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {eventForm.audience !== 'family' && (
        <div>
          <label className="text-sm font-semibold text-gray-700">School / Daycare</label>
          <div className="mt-1 inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {[['both', 'Both'], ['school', 'School only'], ['daycare', 'Daycare only']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setEventForm(f => ({ ...f, selectedClasses: [] })) || setEventAudience(v)}
                className={`px-3 py-1.5 text-sm ${eventAudience === v ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          {eventForm.audience === 'class' && eventAudience === 'daycare' && (
            <p className="text-[11px] text-gray-400 mt-1">Showing daycare classes.</p>
          )}
        </div>
      )}

      {eventForm.audience === 'class' && (
        <div>
          <label className="text-sm font-semibold text-gray-700">Select Classes ({eventForm.selectedClasses.length})</label>
          <input type="text" placeholder="🔍 Search classes..." value={classSearch} onChange={e => setClassSearch(e.target.value)} className="w-full px-4 py-2 my-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
          <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
            {filteredClasses.map(cls => {
              const sel = eventForm.selectedClasses.includes(cls.ClassName);
              return (
                <div
                  key={cls.ClassName}
                  onClick={() => setEventForm({ ...eventForm, selectedClasses: sel ? eventForm.selectedClasses.filter(c => c !== cls.ClassName) : [...eventForm.selectedClasses, cls.ClassName] })}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-amber-500 bg-amber-50' : 'border-gray-300 bg-white hover:border-amber-400'}`}
                >
                  <div className="font-semibold text-gray-900">{cls.ClassName}</div>
                  <div className="text-sm text-gray-600">{cls.StudentCount} students</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {eventForm.audience === 'family' && (
        <div>
          <label className="text-sm font-semibold text-gray-700">Select Families ({eventForm.selectedFamilies.length})</label>
          <input type="text" placeholder="🔍 Search families..." value={familySearch} onChange={e => setFamilySearch(e.target.value)} className="w-full px-4 py-2 my-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
          <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
            {filteredFamilies.map(f => {
              const sel = eventForm.selectedFamilies.includes(f.FamilyID);
              return (
                <div
                  key={f.FamilyID}
                  onClick={() => setEventForm({ ...eventForm, selectedFamilies: sel ? eventForm.selectedFamilies.filter(x => x !== f.FamilyID) : [...eventForm.selectedFamilies, f.FamilyID] })}
                  className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 ${sel ? 'bg-amber-50' : ''}`}
                >
                  <div className="font-semibold text-gray-900">Family {f.FamilyID} - {f.FatherName || f.MotherName || 'Unknown'}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Title:</label>
        <input type="text" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} placeholder="e.g., Christmas Concert 🎄" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Details (optional):</label>
        <textarea rows="3" value={eventForm.body} onChange={e => setEventForm({ ...eventForm, body: e.target.value })} placeholder="Short description shown under the title" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Banner image (optional):</label>
          <input
            id="eventImageFile"
            type="file"
            accept="image/*"
            onChange={e => setEventForm({ ...eventForm, imageFile: e.target.files[0] || null })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
          {eventForm.imageFile ? (
            <p className="text-xs text-gray-600 mt-1">🖼️ {eventForm.imageFile.name} ({(eventForm.imageFile.size / 1024).toFixed(0)} KB)</p>
          ) : (
            <input type="text" value={eventForm.imageUrl} onChange={e => setEventForm({ ...eventForm, imageUrl: e.target.value })} placeholder="…or paste an image URL" className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Link URL (optional):</label>
          <input type="text" value={eventForm.linkUrl} onChange={e => setEventForm({ ...eventForm, linkUrl: e.target.value })} placeholder="https://… (opens on tap)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Show from (optional):</label>
          <input type="date" value={eventForm.startsAt} onChange={e => setEventForm({ ...eventForm, startsAt: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Show until (optional):</label>
          <input type="date" value={eventForm.endsAt} onChange={e => setEventForm({ ...eventForm, endsAt: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
          <p className="text-[11px] text-gray-500 mt-1">Set a date to auto-remove the event after it (it stops showing in the app). Or remove it anytime with Delete in “Active events” below.</p>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center">
        <input type="checkbox" id="eventPush" checked={eventForm.sendPush} onChange={e => setEventForm({ ...eventForm, sendPush: e.target.checked })} className="w-5 h-5 text-amber-600 border-gray-300 rounded" />
        <label htmlFor="eventPush" className="ml-3 flex-1">
          <div className="font-semibold text-gray-900">Send Push Notification</div>
          <div className="text-sm text-gray-600">{eventForm.sendPush ? '📲 Concerned families get notified now' : '📱 Banner only, no notification'}</div>
        </label>
      </div>

      <button
        onClick={previewEvent}
        disabled={!eventForm.title}
        className="w-full bg-white border-2 border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
      >
        👁️ Preview
      </button>
      <button onClick={handlePushEvent} disabled={loading || !eventForm.title} className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold py-4 rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
        {loading ? '⏳ Publishing...' : '🎉 Publish Event'}
      </button>

      {/* Active events — manage / delete */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700">Active events</label>
          <button onClick={loadActiveEvents} className="text-xs text-amber-700 hover:underline">Refresh</button>
        </div>
        {activeEvents === null ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : activeEvents.length === 0 ? (
          <p className="text-xs text-gray-500">No active events.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {activeEvents.map(ev => (
              <div key={ev.event_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {ev.image_url ? (
                    <img src={ev.image_url} alt="" className="h-10 w-10 rounded object-cover shrink-0 cursor-pointer" onClick={() => previewActiveEvent(ev)} />
                  ) : null}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{ev.title}</div>
                    <div className="text-gray-500 text-xs truncate">
                      {ev.recipient_type === 'all' ? 'All families'
                        : ev.recipient_type === 'school' ? 'All school'
                        : ev.recipient_type === 'daycare' ? 'All daycare'
                        : ev.recipient_type === 'class' ? `Class: ${ev.recipient_filter}`
                        : `Family: ${ev.recipient_filter}`}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <button onClick={() => previewActiveEvent(ev)} className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded">👁️ Preview</button>
                  <button
                    onClick={() => handleDeleteEvent(ev.event_id, ev.title)}
                    disabled={loading}
                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderExportFamiliesTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="font-semibold text-blue-900 mb-2">ℹ️ About Export:</p>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Exports all active families to the mobile app</li>
          <li>Generates username and password for each family</li>
          <li>Downloads CSV file with credentials to print</li>
          <li>Run this at the start of each academic year</li>
        </ul>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Academic Year:</label>
        <select
          value={syncYear}
          onChange={(e) => setSyncYear(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          The year the app roster comes from — also used by the absences/behavior/agenda syncs below.
          During a transition this can differ from the system's current year.
        </p>
      </div>

      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
        🔒 Each family gets a <strong>random 6-digit password</strong>. New families get a login;
        families already exported keep their current password (unless you use a Force button).
        The passwords are shown in the downloaded CSV so you can hand them out.
      </p>

      <button
        onClick={() => handleExportFamilies(false)}
        disabled={loading}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? '⏳ Exporting...' : '📤 Export Families Now'}
      </button>

      <button
        onClick={() => handleExportFamilies(true)}
        disabled={loading}
        className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        title="Re-exports everyone and RESETS every family's password to a new random 6-digit code"
      >
        {loading ? '⏳ Exporting...' : '🔑 Force Export — reset ALL passwords'}
      </button>

      <button
        onClick={() => handleExportDaycare(false)}
        disabled={loading}
        className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? '⏳ Exporting...' : '🧸 Export Daycare (children + daycare-only logins)'}
      </button>

      <button
        onClick={() => handleExportDaycare(true)}
        disabled={loading}
        className="w-full mt-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"
        title="Re-provisions daycare-only families and RESETS their passwords to a new random 6-digit code"
      >
        {loading ? '⏳ Exporting...' : '🔑 Force Daycare Export — reset passwords'}
      </button>

      {/* Give / reset one family's login — provisions on the spot if the family
          was never exported, so no full export is needed to onboard one family. */}
      <div className="mt-4 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-1">🔑 Give / reset a family's app login</h4>
        <p className="text-xs text-gray-500 mb-3">
          Username is the Family ID. Sets a new random password and shows it. If the family has no
          login yet (new / re-registered), it's created here instantly — no full export needed.
        </p>
        {/* Search a family by name / id / phone (children shown as subtitle). */}
        <div className="relative mb-2">
          <input
            value={pwSearch}
            onChange={(e) => setPwSearch(e.target.value)}
            placeholder="🔍 Search family by name, ID, or phone…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          {pwSearchResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow max-h-56 overflow-y-auto">
              {pwSearchResults.map((f) => (
                <button
                  key={f.FamilyID}
                  type="button"
                  onClick={() => { setPwReset((p) => ({ ...p, familyId: f.FamilyID, result: null })); setPwSearch(''); setPwSearchResults([]); }}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b last:border-b-0"
                >
                  <div className="text-sm"><span className="font-semibold">{f.FatherName || f.MotherName || f.FamilyID}</span> <span className="text-gray-400">· {f.FamilyID}{f.Phone ? ` · ${f.Phone}` : ''}</span></div>
                  {f.Children && <div className="text-xs text-gray-500">🎒 {f.Children}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={pwReset.familyId}
            onChange={(e) => setPwReset((p) => ({ ...p, familyId: e.target.value, result: null }))}
            placeholder="Family ID (e.g. 7369-51541)"
            className="flex-1 min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            onClick={handleResetPassword}
            disabled={loading || pwReset.checking || !pwReset.familyId.trim()}
            className={`px-4 py-2 text-white text-sm font-semibold rounded-lg disabled:opacity-50 ${pwReset.exists === false ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {loading ? 'Working…'
              : pwReset.checking ? 'Checking…'
              : pwReset.exists === false ? 'Create login & show'
              : pwReset.exists === true ? 'Reset password & show'
              : 'Give / reset login'}
          </button>
        </div>
        {pwReset.familyId.trim().length >= 4 && !pwReset.checking && pwReset.exists !== null && !pwReset.result && (
          <p className={`mt-2 text-xs ${pwReset.exists ? 'text-gray-500' : 'text-green-700'}`}>
            {pwReset.exists
              ? '↺ This family already has a login — it will be reset to a new password.'
              : '＋ No app login yet — one will be created for this family.'}
          </p>
        )}
        {pwReset.result && (
          <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm">
            <div className="mb-1 text-xs font-semibold text-indigo-700">
              {pwReset.result.created ? '✓ New login created' : '✓ Password reset'}
            </div>
            <div><span className="text-gray-600">Username:</span> <strong>{pwReset.result.username}</strong></div>
            <div><span className="text-gray-600">Password:</span> <strong className="font-mono">{pwReset.result.password}</strong></div>
          </div>
        )}
      </div>

      {loading && exportProgress.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-blue-900">{exportProgress.message}</span>
            <span className="text-sm font-semibold text-blue-700">
              {exportProgress.current} / {exportProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${exportProgress.total > 0 ? (exportProgress.current / exportProgress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-blue-700">
            💡 This may take 10-15 minutes for large exports.
          </p>
        </div>
      )}

      {!loading && exportProgress.message === 'Export complete!' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">✅</span>
            <div>
              <p className="font-semibold text-green-900">Export Complete!</p>
              <p className="text-sm text-green-700">
                Successfully exported {exportProgress.current} families.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-3">📊 Export Statistics ({syncYear || '—'}):</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Families enrolled in {syncYear || '—'}:</span>
            <span className="text-sm font-semibold text-blue-600">{exportStats.families}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Students enrolled in {syncYear || '—'}:</span>
            <span className="text-sm font-semibold text-blue-600">{exportStats.students}</span>
          </div>
        </div>
      </div>

      {/* Academic data sync */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h3 className="font-semibold text-indigo-900 mb-1">🎓 Academic Data</h3>
        <p className="text-sm text-indigo-800 mb-3">
          Push every student's <strong>absences and behavior</strong> to the app so parents see them under each child. Safe to re-run — it refreshes each student's records.
        </p>
        <div className="mb-3 text-sm text-indigo-900">
          Syncing academic year: <strong>{syncYear || '—'}</strong>
          <span className="text-xs text-indigo-700"> (change it in the Academic Year selector above)</span>
        </div>
        <button
          onClick={handleSyncAcademic}
          disabled={loading}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Syncing...' : '🔄 Sync Absences & Behavior'}
        </button>

        <button
          onClick={handleSyncBalances}
          disabled={loading}
          className="w-full mt-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Syncing...' : '💰 Sync Family Balances'}
        </button>

        <button
          onClick={handleSyncPaymentPlans}
          disabled={loading}
          className="w-full mt-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Syncing...' : '📅 Sync Payment Plans (reconciles with SIS)'}
        </button>

        <button
          onClick={handleNotifyPayments}
          disabled={loading}
          className="w-full mt-3 bg-white border-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50 font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Sending...' : '🔔 Notify New Payments Now'}
        </button>
        <p className="text-xs text-indigo-700 mt-2">
          Payments are announced automatically every 2 min when <code>PAYMENT_NOTIFY_ENABLED=true</code> (10-min grace so cashier corrections never notify).
        </p>

        <button
          onClick={handleSyncAgenda}
          disabled={loading}
          className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Syncing...' : '📖 Sync Homework Agenda'}
        </button>

        <button
          onClick={handleNotifyAgenda}
          disabled={loading}
          className="w-full mt-3 bg-white border-2 border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Sending...' : '📢 Notify New Agenda (one push per family)'}
        </button>

        <div className="mt-3 border-2 border-red-200 rounded-lg p-3">
          <p className="font-semibold text-red-700 text-sm">🗑️ Remove a student from the app</p>
          <p className="text-xs text-red-600 mt-1 mb-2">
            For duplicates / children deleted in SQL. The student sync never prunes, so a removed child stays on phones until deleted here. Does NOT touch SQL Server.
          </p>
          <div className="flex gap-2">
            <input
              value={mobileStuFamily}
              onChange={e => setMobileStuFamily(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && lookupMobileFamilyStudents()}
              placeholder="Family ID (e.g. 7369-260759)"
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={lookupMobileFamilyStudents}
              disabled={loading || !mobileStuFamily.trim()}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm rounded-lg disabled:opacity-50"
            >
              Look up
            </button>
          </div>
          {mobileStuList && mobileStuList.length === 0 && (
            <p className="text-xs text-gray-500 mt-2">No students in the app DB for this family.</p>
          )}
          {mobileStuList && mobileStuList.length > 0 && (
            <div className="mt-2 border rounded-lg divide-y divide-gray-100">
              {mobileStuList.map(s => (
                <div key={s.student_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-gray-500">{s.student_id}</span>{' '}
                    <span className="font-medium">{s.first_name} {s.last_name}</span>{' '}
                    <span className="text-gray-500">· {s.class_name || '—'}</span>
                    {!s.is_active && <span className="ml-1 text-xs text-amber-600">(inactive)</span>}
                  </div>
                  <button
                    onClick={() => deleteMobileStudent(s.student_id, `${s.first_name} ${s.last_name}`)}
                    disabled={loading}
                    className="shrink-0 px-2 py-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grade blocks */}
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
        <h3 className="font-semibold text-rose-900 mb-1">🔒 Grade Blocks</h3>
        <p className="text-sm text-rose-800 mb-3">
          Families carrying an outstanding balance are suggested below. Blocking <strong>removes their report cards from the app</strong> and shows the message instead. Unblock, then re-push report cards to restore.
        </p>
        <label className="block text-xs font-medium text-rose-900 mb-1">Message shown to blocked families</label>
        <input
          value={blockMsg}
          onChange={(e) => setBlockMsg(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-rose-300 rounded-lg text-sm"
        />

        {/* Manual search — block ANY family, not just the outstanding suggestions */}
        <label className="block text-xs font-medium text-rose-900 mb-1">Find a family to block (name or ID)</label>
        <input
          value={blockSearch}
          onChange={(e) => setBlockSearch(e.target.value)}
          placeholder="Search all families…"
          className="w-full mb-2 px-3 py-2 border border-rose-300 rounded-lg text-sm"
        />
        {blockSearch.trim() && (
          <div className="mb-3 divide-y divide-gray-100 bg-white rounded-lg border border-gray-200">
            {blockMatches.map((f) => {
              const isBlocked = blockedIds.has(String(f.FamilyID));
              return (
                <div key={f.FamilyID} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                  <span className="min-w-0 truncate text-gray-800">
                    {f.FatherName || f.MotherName || 'Family'} <span className="text-xs text-gray-400">#{f.FamilyID}</span>
                    {isBlocked ? <span className="ml-2 text-rose-600 text-xs font-semibold">Blocked</span> : null}
                  </span>
                  {isBlocked ? (
                    <button onClick={() => unblockFamily(f.FamilyID)}
                      className="px-3 py-1 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex-shrink-0">
                      Unblock
                    </button>
                  ) : (
                    <button onClick={() => blockFamily(f.FamilyID)}
                      className="px-3 py-1 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex-shrink-0">
                      Block
                    </button>
                  )}
                </div>
              );
            })}
            {blockMatches.length === 0 && <p className="text-center text-gray-400 text-sm py-3">No families match.</p>}
          </div>
        )}

        <p className="text-xs text-rose-700 mb-1 font-medium">Suggested (outstanding balance) & currently blocked</p>
        <div className="max-h-72 overflow-y-auto divide-y divide-rose-100 bg-white rounded-lg border border-rose-100">
          {blockRows.map((f) => {
            const usd = Math.round(Number(f.OutstandingUSD) || 0);
            const lbp = Math.round(Number(f.OutstandingLBP) || 0);
            return (
              <div key={f.FamilyID} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 truncate">
                    {f.FatherName || f.MotherName || 'Family'} <span className="text-xs text-gray-400">#{f.FamilyID}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {usd ? `$${usd}` : ''}{usd && lbp ? ' · ' : ''}{lbp ? `${lbp.toLocaleString()} LBP` : ''}
                    {f.Blocked ? <span className="ml-2 text-rose-600 font-semibold">Blocked</span> : null}
                  </div>
                </div>
                {f.Blocked ? (
                  <button onClick={() => unblockFamily(f.FamilyID)}
                    className="px-3 py-1 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex-shrink-0">
                    Unblock
                  </button>
                ) : (
                  <button onClick={() => blockFamily(f.FamilyID)}
                    className="px-3 py-1 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex-shrink-0">
                    Block
                  </button>
                )}
              </div>
            );
          })}
          {blockRows.length === 0 && <p className="text-center text-gray-400 text-sm py-4">No outstanding or blocked families.</p>}
        </div>
      </div>

      {/* Bus tracking sync */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="font-semibold text-emerald-900 mb-1">🚌 Bus Tracking</h3>
        <p className="text-sm text-emerald-800 mb-3">
          Push this year's <strong>active bus enrollments</strong> to the app (unlocks the Bus tab and sets each child's assigned bus). Then pull any <strong>drop-off pins</strong> parents set, into SQL Server for the web view.
        </p>
        <button
          onClick={handleSyncBusSubscriptions}
          disabled={loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Syncing...' : '🚌 Sync Bus Subscriptions'}
        </button>
        <button
          onClick={handlePullBusDropoffs}
          disabled={loading}
          className="w-full mt-3 bg-white border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? '⏳ Pulling...' : '📍 Pull Drop-off Pins'}
        </button>
        <p className="text-xs text-emerald-700 mt-2">
          Drop-off pins also pull automatically every 15 min when <code>BUS_DROPOFF_PULL_ENABLED=true</code> on the server.
        </p>
      </div>
    </div>
  );

  // Delete a message batch everywhere: parents' app feed + any R2 attachment +
  // the school history log.
  const handleDeleteMessage = async (msg) => {
    if (!window.confirm(`Delete "${msg.Title}"?\n\nThis removes it from the app for all recipients and deletes any attached file. This cannot be undone.`)) return;
    setLoading(true);
    try {
      const r = await apiCall('/mobile/messages/delete', {
        method: 'POST',
        body: JSON.stringify({ sendId: msg.SendId || null, title: msg.Title, type: msg.MessageType }),
      });
      showSuccess(`Deleted${r.attachmentsRemoved ? ` · ${r.attachmentsRemoved} attachment(s) removed` : ''}`);
      loadMessageHistory();
    } catch (e) {
      showError('Delete failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const announcementAudienceLabel = (a) => {
    if (a.recipient_type === 'school') return 'School families';
    if (a.recipient_type === 'daycare') return 'Daycare families';
    if (a.recipient_type === 'class') return `Classes: ${a.recipient_filter || '—'}`;
    return 'All families';
  };

  const renderMessageHistoryTab = () => (
    <div className="space-y-4">
      {/* Announcements (green megaphone) — separate store from messages, shown here too */}
      {announcementHistory.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">📢 Announcements</h4>
          {announcementHistory.map((a) => (
            <div key={`ann-${a.document_id}`} className="border border-green-200 bg-green-50/40 rounded-lg p-4 hover:bg-green-50">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{a.title}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">announcement</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{announcementAudienceLabel(a)}</span>
                    <span className="text-xs text-gray-400">📄 PDF</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => handleDeleteAnnouncement(a)}
                    disabled={loading}
                    className="flex items-center gap-1 text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50"
                    title="Delete for everyone (app feed + PDF)"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div className="border-t border-gray-200 pt-2" />
        </div>
      )}

      {messageHistory.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{announcementHistory.length > 0 ? 'No messages sent yet' : 'Nothing sent yet'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messageHistory.map((msg, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{msg.Title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold
                      ${msg.MessageType === 'urgent' ? 'bg-red-100 text-red-800' : ''}
                      ${msg.MessageType === 'payment' ? 'bg-yellow-100 text-yellow-800' : ''}
                      ${msg.MessageType === 'absence' ? 'bg-orange-100 text-orange-800' : ''}
                      ${msg.MessageType === 'announcement' ? 'bg-green-100 text-green-800' : ''}
                      ${msg.MessageType === 'general' ? 'bg-blue-100 text-blue-800' : ''}
                    `}>
                      {msg.MessageType}
                    </span>
                    <span className="text-xs text-gray-400">{msg.PushSent ? '📲 pushed' : '📱 in-app'}</span>
                  </div>
                  {msg.Message ? (
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap line-clamp-3">{msg.Message}</p>
                  ) : null}
                  <div className="text-xs text-gray-400 mt-2">
                    {new Date(msg.SentAt).toLocaleString()} · {msg.RecipientCount} {msg.RecipientCount === 1 ? 'family' : 'families'}
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-1">
                  <button
                    onClick={() => previewHistory(msg)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-sm font-semibold"
                    title="Preview how it looks in the app"
                  >
                    👁️ Preview
                  </button>
                  <button
                    onClick={() => handleDeleteMessage(msg)}
                    disabled={loading}
                    className="flex items-center gap-1 text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50"
                    title="Delete for everyone (app + attachment)"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={loadMessageHistory}
        className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
      >
        🔄 Refresh History
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-gray-50 rounded-2xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <AppPreview {...preview} />
            <button onClick={() => setPreview(null)} className="mt-4 w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold text-gray-700">Close</button>
          </div>
        </div>
      )}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 shadow-lg">
        <h1 className="text-3xl font-bold mb-2">📱 Mobile App Manager</h1>
        <p className="text-blue-100">Manage messaging, documents, and family exports</p>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          <button 
            onClick={() => setActiveTab('send-message')}
            className={`pb-4 px-1 font-semibold transition-colors ${
              activeTab === 'send-message' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📤 Send
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-4 px-1 font-semibold transition-colors ${
              activeTab === 'documents'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📁 Documents
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`pb-4 px-1 font-semibold transition-colors ${
              activeTab === 'export' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📥 Export Families
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`pb-4 px-1 font-semibold transition-colors ${
              activeTab === 'history' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📜 Message History
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'send-message' && renderSendMessageTab()}
        {/* events are now a type inside the Send tab */}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'export' && renderExportFamiliesTab()}
        {activeTab === 'history' && renderMessageHistoryTab()}
      </div>
    </div>
  );
};

export default MobileAppManager;